"use client";

import React from "react";

import SafetyBadge, { type SafetyBadgeProps } from "@/components/common/SafetyBadge/SafetyBadge";
import Tag from "@/components/common/Tag/Tag";
import {
  ButtonNova,
  ButtonSize,
  ButtonLevel,
  ButtonNovaProps,
} from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementMen } from "@/components/ui-kit/TextElementMen/TextElementMen";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { PromoItemList } from "@/types/promoData";
import { cn, scrollToId } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";
export interface BannerProps {
  id?: string;
  image?: {
    mobile: { src: string; alt?: string } | null;
    tablet?: { src: string; alt?: string } | null;
    desktop: { src: string; alt?: string } | null;
  };
  tag?: {
    line: string;
    classes?: string;
  };
  heading?: {
    text: string;
    className?: string;
    classes?: Record<string, string> & { default?: string };
  };
  subtitle?: {
    text: string;
    className?: string;
    classes?: Record<string, string> & { default?: string };
  };
  button?: {
    size?: ButtonSize;
    level?: ButtonLevel;
    label: string;
    ctaLink?: string;
    className?: string;
    copyClassName?: string;
  };
  scrollToProductsButton?: Omit<ButtonNovaProps, "onClick" | "ctaLink"> & {
    smoothScrollToHash?: string;
  };
  safetyBadge?: SafetyBadgeProps;
  contentClassName?: string;
  tagClassName?: string;
  imageClassName?: string;
  className?: string;
  theme?: ThemeTypes;
  verticalClasses?: string;
  horizontalClasses?: string;
  breakpoints?: {
    tablet: number;
    desktop: number;
  };
  hasShadow?: boolean;
  shadowClasses?: string;
  promoData?: PromoItemList;
}

const DEFAULT_IMAGE: BannerProps["image"] = {
  mobile: null,
  tablet: null,
  desktop: null,
};

const Banner2: React.FC<BannerProps> = ({
  id,
  image = DEFAULT_IMAGE,
  tag,
  heading,
  subtitle,
  button,
  scrollToProductsButton,
  className = "",
  contentClassName = "p-6 md:p-8 lg:p-10 xl:p-[156px]",
  tagClassName = "mb-6 block",
  safetyBadge,
  imageClassName = "",
  breakpoints = { tablet: 768, desktop: 1024 },
  theme = "master",
  horizontalClasses = "justify-center",
  verticalClasses = "items-end",
  hasShadow = false,
  shadowClasses = "bottom-0 left-0 w-full h-1/2 bg-[linear-gradient(180deg,rgba(27,27,27,0)_0%,rgba(27,27,27,0.85)_100%)] lg:bg-none",
  promoData = null,
}) => {
  const { mobile, tablet, desktop } = image;
  const isMen = theme === "men";
  const TextElement = isMen ? TextElementMen : TextElementNova;
  const tagObject = promoData?.tag || tag;
  const buttonObject = promoData?.button || button;

  return (
    <div id={id} className={cn("relative w-full h-full", className)}>
      {mobile && desktop ? (
        <>
          {hasShadow && <div className={cn("absolute", shadowClasses)}></div>}
          <Picture
            src={mobile.src}
            sources={[
              {
                media: `(min-width: ${breakpoints.desktop}px)`,
                srcSet: (desktop || tablet || mobile).src,
              },
              {
                media: `(min-width: ${breakpoints.tablet}px)`,
                srcSet: (tablet || mobile).src,
              },
            ]}
            alt={(mobile.alt || heading?.text)?.replace(/<[^>]*>/g, "").trim()}
            className={cn("w-full h-full object-cover max-h-[800px]", imageClassName)}
            /* Usually the page hero, so it must not be lazy. */
            loading="eager"
          />
        </>
      ) : null}

      <div
        className={cn(
          contentClassName,
          "max-w-[1600px] mx-auto absolute inset-0 flex",
          verticalClasses,
          horizontalClasses
        )}
      >
        <div>
          {tagObject ? (
            <div className={tagClassName}>
              <Tag data={tagObject} />
            </div>
          ) : null}
          {heading?.text && (
            <TextElement
              element="h1"
              text={heading.text}
              className={heading.className}
              classes={
                heading.classes || {
                  default: "Title-20-Super-Normal",
                  [breakpoints.tablet]: "Title-20-Super-Normal",
                  [breakpoints.desktop]: "Title-30-Super-Normal",
                }
              }
            />
          )}
          {subtitle && (
            <TextElement
              element="p"
              text={subtitle.text}
              className={subtitle.className}
              classes={
                subtitle.classes || {
                  default: "Body-20-Dia-Normal",
                  [breakpoints.tablet]: "Body-30-Dia-Normal",
                  [breakpoints.desktop]: "Body-30-Dia-Normal",
                }
              }
            />
          )}
          {buttonObject && (
            <div
              className={cn(
                "pt-4 pb-8",
                (buttonObject as { wrapperClassName?: string } | undefined)?.wrapperClassName
              )}
            >
              <ButtonNova
                size={
                  (buttonObject as ButtonNovaProps)?.size ||
                  ((buttonObject as ButtonNovaProps)?.level === "Textlink" ? "Small" : "Default")
                }
                level={buttonObject?.level || "Primary"}
                label={buttonObject?.label}
                ctaLink={buttonObject?.ctaLink}
                className={buttonObject?.className}
                copyClassName={button?.copyClassName || ""}
                icon="Right"
                theme={theme}
                trackObject={{
                  destination: buttonObject?.ctaLink,
                  text: buttonObject?.label,
                  type: "button",
                }}
              />
            </div>
          )}
          {scrollToProductsButton && (
            <div className={`pt-4 pb-8`}>
              <ButtonNova
                onClick={() => {
                  scrollToId(scrollToProductsButton?.smoothScrollToHash ?? "products");
                }}
                label={scrollToProductsButton.label}
              />
            </div>
          )}
          {safetyBadge && (
            <div className="flex justify-center md:justify-start bg-[linear-gradient(181deg,_rgba(27,27,27,0)_0.52%,_rgba(27,27,27,0.85)_95.87%)] md:bg-none absolute md:relative bottom-0 left-0 right-0 md:inset-auto w-full px-4 md:px-0 py-8 md:py-0">
              <SafetyBadge {...safetyBadge} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Banner2;
